import { Fragment, useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import HeaderPage from "../../../shared/components/HeaderPage/HeaderPage";
import SimpleTable from "../../../shared/components/common/ui/SimpleTable";
import AllPricingModal from "../components/MessagePricing/AllPricingModal";
import MessagePreviewScreen from "../../../shared/components/common/ui/MessagePreviewScreen/MessagePreviewScreen";

import { useTemplatesData } from "../hooks/useTemplatesData";
import { getTemplatesColumns } from "./TemplatesTableColumns";

import { TemplateDelete } from "../../../services/api/templates/TemplateDelete";
import { TemplateHidden } from "../../../services/api/templates/TemplateHidden";

import TemplatesEmptyState from "../components/TemplatesEmptyState/TemplatesEmptyState";

import { notify } from "../../../shared/utils/utils";
import { SendMessageModal } from "../components/SendMessageModal/SendMessageModal";
import { useSendMessage } from "../hooks/useSendMessage";

export default function TemplatesPageList() {
  const { templates, loading, isFetching, refetch } = useTemplatesData();
  const { isLoadingSendMessage, sendMessage } = useSendMessage();

  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sendMessageModalOpen, setSendMessageModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const columns = useMemo(
    () => getTemplatesColumns(() => setIsPricingOpen(true)),
    [],
  );

  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetch();
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, refetch, navigate, location.pathname]);

  // ==========================
  // AÇÕES
  // ==========================

  const handleAdd = () => {
    navigate("/modelos/canal");
  };

  const buildPreviewFromRow = (row) => {
    try {
      const source = row?.messageJson || row?.message_json;
      const parsed = typeof source === "string" ? JSON.parse(source) : source;

      const messages = Array.isArray(parsed?.messages)
        ? parsed.messages.map((item, index) => {
            const rawMessage = item?.message;
            const rawMessageObject =
              typeof rawMessage === "object" && rawMessage !== null
                ? rawMessage
                : {};
            const content =
              typeof rawMessage === "string"
                ? rawMessage
                : rawMessageObject?.content || "";
            const buttonsFromMessage = Array.isArray(rawMessageObject?.buttons)
              ? rawMessageObject.buttons
              : [];
            const buttonsFromItem = Array.isArray(item?.buttons)
              ? item.buttons
              : [];

            return {
              ...rawMessageObject,
              id: item?.id || `msg-${index}`,
              type: rawMessageObject?.type || "text",
              content,
              fromMe: item?.fromMe ?? true,
              buttons: buttonsFromMessage.length
                ? buttonsFromMessage
                : buttonsFromItem,
            };
          })
        : [];

      return {
        title: row?.title || "Pré-visualização",
        channel: row?.channel_type || "",
        messages,
      };
    } catch (error) {
      console.error("Erro ao montar preview do template:", error);
      return {
        title: row?.title || "Pré-visualização",
        channel: row?.channel_type || "",
        messages: [],
      };
    }
  };

  const handlePreview = (row) => {
    setPreviewTemplate(buildPreviewFromRow(row));
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewTemplate(null);
  };

  const handleOpenSendMessage = (row) => {
    console.log("Enviar mensagem para template:", row);
    setPreviewTemplate(row);
    setSendMessageModalOpen(true);
  };

  const handleSendMessage = async (phone) => {
    if (!previewTemplate) {
      notify("Error", "Template não encontrado para envio.", true);
      return;
    }

    const result = await sendMessage(
      previewTemplate,
      phone,
      previewTemplate.channel_id,
    );

    if (result.success) {
      notify("Success", "Mensagem de teste enviada com sucesso!", true);
      setSendMessageModalOpen(false);
    } else {
      notify(
        "Error",
        "Erro ao enviar mensagem de teste. Tente novamente.",
        true,
      );
    }
  }

  const handleDelete = async (row) => {
    const confirmed = window.confirm(
      `Deseja remover permanentemente o template "${row.title || "este template"}"?`,
    );

    if (!confirmed) return;

    try {
      const result = await TemplateDelete({ id_template: row.id });

      if (result.status === "success") {
        notify("Success", "Modelo deletado com sucesso!", true);
        await refetch();
      } else {
        notify("Error", "Erro ao deletar modelo.", true);
      }
    } catch (error) {
      console.error(error);
      notify("Error", "Erro ao deletar modelo.", true);
    }
  };

  const handleToggleVisibility = async (row) => {
    const actionLabel = row.visibility ? "ocultar" : "exibir";

    const confirmed = window.confirm(
      `Deseja ${actionLabel} o template "${row.title || "este template"}"?`,
    );

    if (!confirmed) return;

    try {
      const result = await TemplateHidden({
        id_template: row.id,
        visibility: !row.visibility,
      });

      if (result.status === "success") {
        notify(
          "Success",
          `Template ${row.visibility ? "ocultado" : "exibido"} com sucesso!`,
          true,
        );
        await refetch();
      } else {
        notify("Error", "Erro ao alterar visibilidade.", true);
      }
    } catch (error) {
      console.error(error);
      notify("Error", "Erro ao alterar visibilidade.", true);
    }
  };

  // ==========================
  // AÇÕES DA TABELA
  // ==========================

  const templateActions = [
    {
      label: "Visualizar",
      icon: "view",
      color: "primary",
      isVisible: () => true,
      onClick: handlePreview,
    },
    {
      label: "Ocultar",
      icon: "view-off",
      color: "warning",
      isVisible: (row) => row.visibility === true,
      onClick: handleToggleVisibility,
    },
    {
      label: "Exibir",
      icon: "view",
      color: "success",
      isVisible: (row) => row.visibility === false,
      onClick: handleToggleVisibility,
    },
    {
      label: "Excluir",
      icon: "delete",
      color: "error",
      isVisible: () => true,
      onClick: handleDelete,
    },
    {
      label: "Enviar mensagem",
      icon: "send",
      color: "primary",
      isVisible: (row) =>
        row.channel_type === "whatsapp_api" && row.status === "APPROVED",
      onClick: handleOpenSendMessage,
    },
  ];

  return (
    <Fragment>
      <Box width="100%" height="100vh">
        <HeaderPage
          variant="listing"
          title="Modelos"
          description="Crie e gerencie mensagens reutilizáveis para os canais da plataforma."
        />

        {!loading && templates.length === 0 ? (
          <TemplatesEmptyState onCreate={handleAdd} />
        ) : (
          <SimpleTable
            tableName="Templates"
            loading={loading}
            data={templates}
            columns={columns}
            actions={templateActions}
            redirect={handleAdd}
            redirectText="Adicionar Modelo"
            isFetching={isFetching}
            onRefresh={refetch}
            variant="listing"
          />
        )}
      </Box>

      <AllPricingModal
        open={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
      />

      <SendMessageModal
        isOpen={sendMessageModalOpen}
        onClose={() => setSendMessageModalOpen(false)}
        template={previewTemplate}
        handleSendMessage={(phone) => handleSendMessage(phone)}
        isLoading={isLoadingSendMessage}
      />

      <Dialog open={previewOpen} onClose={closePreview} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {previewTemplate?.title || "Pré-visualização"}
          <IconButton
            aria-label="Fechar"
            onClick={closePreview}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ width: "100%", minHeight: 420 }}>
            <MessagePreviewScreen
              channel={previewTemplate?.channel}
              messages={previewTemplate?.messages || []}
              disabledButtons
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
